import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Send, CalendarPlus } from "lucide-react";
import api, { apiError, downloadCsv } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RATING_LABELS, RatingBadge, ratingStyle } from "@/components/ReviewsSection";

const statusBadge = {
  pending_payment: "bg-foreground/10 text-foreground border-foreground/30",
  confirmed: "bg-primary/15 text-primary border-primary/30",
  completed: "bg-primary/10 text-primary border-primary/30",
  cancelled: "bg-muted text-muted-foreground border-border",
};

const RescheduleDialog = ({ booking, onDone }) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !date) return;
    setTime("");
    api.get(`/practitioners/${booking.practitioner_id}/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [open, date, booking.practitioner_id]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/bookings/${booking.id}/reschedule`, { date, time });
      toast.success(`Rescheduled to ${date} at ${time}`);
      setOpen(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`reschedule-booking-${booking.id}`} onClick={() => setOpen(true)}>
        Reschedule
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Pick a new time</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{booking.service.name} with {booking.business_name} — currently {booking.date} at {booking.time}</p>
          <input type="date" data-testid="reschedule-date-input" value={date}
            min={new Date().toISOString().slice(0, 10)}
            max={new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground" />
          {date && (slots.length === 0 ? (
            <p className="text-sm text-muted-foreground">No times available this day.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((s) => (
                <button key={s} type="button" data-testid={`reschedule-slot-${s.replace(":", "")}`} onClick={() => setTime(s)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${time === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {s}
                </button>
              ))}
            </div>
          ))}
          <Button data-testid="reschedule-confirm-btn" className="rounded-full" disabled={!date || !time || busy} onClick={submit}>
            {busy ? "Moving…" : "Confirm new time"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

const cancelBooking = async (booking, onDone) => {
  const hrs = (new Date(`${booking.date}T${booking.time}:00Z`) - new Date()) / 36e5;
  const policy = hrs >= 24
    ? `You'll receive a full refund of $${booking.amount} CAD.`
    : "This session starts within 24 hours — per the cancellation policy, no refund applies.";
  if (!window.confirm(`Cancel this session?\n\n${policy}`)) return;
  try {
    const { data } = await api.post(`/bookings/${booking.id}/cancel`);
    toast.success(data.refunded ? `Cancelled — $${data.refund_amount} CAD refunded.` : "Cancelled — no refund (within 24 hours).");
    onDone();
  } catch (e) { toast.error(apiError(e)); }
};

const RateDialog = ({ booking, onDone }) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", { booking_id: booking.id, rating, comment });
      setOpen(false);
      if (data.status === "held") {
        toast.info("Your 1-star review is on a private hold — you and the practitioner can resolve it privately. If unresolved in 7 days, the O.R.E.C team steps in.");
      } else {
        toast.success("Thank you for your review");
      }
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`rate-booking-${booking.id}`} onClick={() => setOpen(true)}>
        Rate your session
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">How was your session?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{booking.service.name} with {booking.business_name}</p>
          <div className="grid grid-cols-3 gap-2 my-2" data-testid="rating-picker">
            {[1, 2, 3].map((r) => (
              <button key={r} type="button" data-testid={`rating-option-${r}`} onClick={() => setRating(r)}
                className={`rounded-lg border py-4 text-center transition-colors ${rating === r ? ratingStyle(r) : "border-border text-muted-foreground hover:text-foreground"}`}>
                <p className="font-display text-2xl">{r}</p>
                <p className="text-xs mt-1">{RATING_LABELS[r]}</p>
              </button>
            ))}
          </div>
          {rating === 1 && (
            <p className="text-xs text-muted-foreground border border-border rounded-lg p-3">
              A 1-star review starts a private resolution: it stays hidden while you and the practitioner sort things out. If it isn't resolved within 7 days, the O.R.E.C team reviews it personally.
            </p>
          )}
          <Textarea data-testid="rating-comment" rows={3} placeholder="A few words about your experience (optional)"
            value={comment} onChange={(e) => setComment(e.target.value)} />
          <Button data-testid="rating-submit" disabled={!rating || busy} onClick={submit} className="rounded-full">Submit review</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ResolutionThread = ({ review, onChanged, canResolve }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/reviews/${review.id}/messages`, { text });
      setText("");
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/${review.id}/resolve`, {});
      toast.success("Marked resolved — your review is now public again.");
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`resolution-thread-${review.id}`}>
      <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
        {(review.messages || []).length === 0 && <p className="text-xs text-muted-foreground">No messages yet — start the conversation.</p>}
        {(review.messages || []).map((m, i) => (
          <div key={i} className={`rounded-lg p-3 text-sm ${m.sender_role === "practitioner" ? "bg-secondary" : "bg-primary/10"}`}>
            <p className="text-xs text-muted-foreground mb-1">{m.sender_name} · {m.sender_role}</p>
            <p className="text-foreground/90">{m.text}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Textarea rows={1} data-testid={`resolution-input-${review.id}`} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Write a private message…" className="min-h-[40px]" />
        <Button size="icon" variant="outline" data-testid={`resolution-send-${review.id}`} disabled={busy} onClick={send}><Send size={14} /></Button>
      </div>
      {canResolve && (
        <Button size="sm" variant="outline" className="rounded-full" data-testid={`resolution-resolve-${review.id}`} disabled={busy} onClick={resolve}>
          Mark resolved & restore review
        </Button>
      )}
    </div>
  );
};

export default function MyBookings() {
  const [bookings, setBookings] = useState(null);
  const [threadReview, setThreadReview] = useState(null);

  const load = useCallback(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    if (threadReview) {
      const fresh = bookings?.find((b) => b.review?.id === threadReview.id)?.review;
      if (fresh) setThreadReview(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  if (bookings === null) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
      <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-2">My bookings</h1>
      <p className="text-sm text-muted-foreground mb-10">Your sessions, confirmations, and reviews.</p>

      {bookings.length === 0 ? (
        <div className="text-center py-20" data-testid="bookings-empty">
          <p className="font-display text-2xl text-foreground mb-2">No bookings yet</p>
          <p className="text-sm text-muted-foreground mb-6">Find someone wonderful and book your first session.</p>
          <Button asChild className="rounded-full px-8"><Link to="/browse">Explore practitioners</Link></Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="bookings-list">
          {bookings.map((b) => (
            <div key={b.id} className="silk-card rounded-lg p-5" data-testid={`booking-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-display text-xl text-foreground">{b.service.name}</p>
                  <p className="text-sm text-muted-foreground">
                    with <Link to={`/practitioner/${b.practitioner_id}`} className="cream-link">{b.business_name}</Link> · {b.date} at {b.time}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-primary">${b.amount} CAD</span>
                  <Badge variant="outline" className={`font-normal border ${statusBadge[b.status] || ""}`} data-testid={`booking-status-${b.id}`}>
                    {b.status.replace("_", " ")}
                  </Badge>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {b.status === "confirmed" && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`add-to-calendar-${b.id}`}
                      onClick={() => downloadCsv(`/bookings/${b.id}/calendar.ics`, `orec-session-${b.date}.ics`).then(() => toast.success("Calendar file downloaded — open it to add the session")).catch((e) => toast.error(apiError(e)))}>
                      <CalendarPlus size={14} className="mr-1.5" /> Add to calendar
                    </Button>
                    <RescheduleDialog booking={b} onDone={load} />
                    <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground hover:text-foreground"
                      data-testid={`cancel-booking-${b.id}`} onClick={() => cancelBooking(b, load)}>
                      Cancel
                    </Button>
                  </>
                )}
                {b.status === "cancelled" && (
                  <p className="text-xs text-muted-foreground" data-testid={`refund-note-${b.id}`}>
                    {b.refund_status === "refunded" ? `Refunded $${b.refund_amount} CAD` : "No refund (cancelled within 24 hours)"}
                  </p>
                )}
                {["confirmed", "completed"].includes(b.status) && !b.review && <RateDialog booking={b} onDone={load} />}
                {b.review && b.review.status === "published" && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">Your review: <RatingBadge rating={b.review.rating} /></div>
                )}
                {b.review && ["held", "escalated"].includes(b.review.status) && (
                  <button data-testid={`open-thread-${b.review.id}`} onClick={() => setThreadReview(b.review)}
                    className="text-sm text-foreground/80 flex items-center gap-1.5 hover:opacity-80">
                    <MessageCircle size={14} />
                    {b.review.status === "held" ? "In private resolution — open thread" : "Escalated to the team — view thread"}
                  </button>
                )}
                {b.review?.status === "dismissed" && <p className="text-xs text-muted-foreground">Review closed by admin after escalation.</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!threadReview} onOpenChange={(v) => { if (!v) setThreadReview(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Private resolution</DialogTitle></DialogHeader>
          {threadReview && (
            <>
              <p className="text-xs text-muted-foreground -mt-2">
                Only you, {threadReview.business_name}, and the O.R.E.C team can see this. Resolving restores your review publicly.
              </p>
              <ResolutionThread review={threadReview} canResolve={threadReview.status !== "dismissed"} onChanged={load} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
